import { Box, CircularProgress, Typography } from '@mui/material';
import { ContentWrapper } from '@/components/ContentWrapper';
import {
    FONT_WEIGHT,
    Heading,
    LINE_HEIGHT,
    PageSubHeading,
    TEXT_SIZE
} from '@/typography/Typography';
import { SicknessReport } from '@/components/reports/SicknessReport';
import { SicknessDashboard } from '@/components/reports/SicknessDashboard';
import { SickWithPayReport } from '@/components/reports/SickWithPayReport';
import { EmployeeChurnReport } from '@/components/reports/EmployeeChurnReport';
import { OvertimeReport } from '@/components/reports/OvertimeReport';
import { HoursWorkedReport } from '@/components/reports/HoursWorkedReport';
import { DynamicReportViewer } from '@/components/reports/DynamicReportViewer';
import { ViewSwitch } from '@/components/shared/ViewSwitch';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@/contexts/user/UserContext';
import { UserRoles } from '@/models/company';
import { getCompanyIdFromLocalStorage } from '@/services/companyService';
import {
    useReportDefinitions,
    useToggleDefinition,
    useToggleDraftDefinition
} from '@/services/reportAIQuery';
import { useHandleResponse } from '@/lib/hooks/useHandleResponse';

const STATIC_COMPONENT_MAP: Record<string, React.ComponentType> = {
    dashboard: SicknessDashboard,
    sickness_report: SicknessReport,
    sick_with_pay: SickWithPayReport,
    employee_churn: EmployeeChurnReport,
    overtime: OvertimeReport,
    hours_worked: HoursWorkedReport
};

const STATIC_REPORT_ORDER = Object.keys(STATIC_COMPONENT_MAP);

export const ReportsDashboard = () => {
    const navigate = useNavigate();
    const { user } = useUser();

    const isGlobalAdmin = user?.role === UserRoles.SuperAdmin;
    const companyId = getCompanyIdFromLocalStorage();

    const {
        data: definitions,
        isLoading: definitionsLoading,
        error: definitionsFetchError
    } = useReportDefinitions(companyId ?? '', !!companyId);
    const { mutate: toggleDefinition } = useToggleDefinition();
    const { mutate: toggleDraftDefinition } = useToggleDraftDefinition();

    useHandleResponse({ error: definitionsFetchError });

    const viewSwitchItems = [...(definitions ?? [])]
        .sort((a, b) => {
            const ai = a.staticReportKey
                ? STATIC_REPORT_ORDER.indexOf(a.staticReportKey)
                : Infinity;
            const bi = b.staticReportKey
                ? STATIC_REPORT_ORDER.indexOf(b.staticReportKey)
                : Infinity;
            return ai !== bi
                ? ai - bi
                : (a.reportNumber ?? 0) - (b.reportNumber ?? 0);
        })
        .map((def) => {
            const label =
                isGlobalAdmin && !def.isEnabled && def.isEnabledAsDraft
                    ? `${def.name} (Draft)`
                    : def.name;
            const StaticComp = def.staticReportKey
                ? STATIC_COMPONENT_MAP[def.staticReportKey]
                : undefined;
            const routeComponent = StaticComp ? (
                <StaticComp />
            ) : (
                <DynamicReportViewer
                    key={def.reportId}
                    definition={def}
                    companyId={companyId ?? ''}
                    isSuperAdmin={isGlobalAdmin}
                    onDeactivate={() => {
                        navigate('..', {
                            relative: 'path',
                            replace: true
                        });
                        if (def.isEnabled) {
                            toggleDefinition(def.reportId);
                        } else {
                            toggleDraftDefinition(def.reportId);
                        }
                    }}
                    onToggleMode={() => {
                        navigate('..', {
                            relative: 'path',
                            replace: true
                        });
                        if (def.isEnabled) {
                            toggleDraftDefinition(def.reportId);
                        } else {
                            toggleDefinition(def.reportId);
                        }
                    }}
                />
            );
            return {
                primaryText: label,
                route: def.staticReportKey ?? `dynamic_report_${def.reportId}`,
                badgeText: '',
                routeComponent
            };
        });

    const [tabIndex, setTabIndex] = useState<number | null>(null);

    return (
        <ContentWrapper>
            <>
                <Box m={1} display={'flex'} justifyContent={'space-between'}>
                    <Typography variant="h4" marginBottom="5px">
                        <Heading
                            fontSize={TEXT_SIZE.MEDIUM_LARGE}
                            fontWeight={FONT_WEIGHT.MEDIUM}
                            lineHeight={LINE_HEIGHT.XLARGE}
                        >
                            Reports
                        </Heading>
                        <PageSubHeading>
                            Access your reports here.
                        </PageSubHeading>
                    </Typography>
                </Box>
                {definitionsLoading && (
                    <Box display="flex" justifyContent="center" py={6}>
                        <CircularProgress size={32} />
                    </Box>
                )}

                {/* Keyed off the items, not the error flag: definitions are cached for a
                    minute with no retry, so a failed background refetch would otherwise
                    tear down the tabs and the report being read. */}
                {!definitionsLoading && viewSwitchItems.length > 0 && (
                    <ViewSwitch
                        itemList={viewSwitchItems}
                        setSelectedIndex={setTabIndex}
                        selectedIndex={tabIndex}
                    />
                )}
            </>
        </ContentWrapper>
    );
};
